import React, { useState } from 'react';
import { Link } from 'react-router-dom';

import {
  ACTION_LABELS,
  TABLE_LABELS,
  actorOf,
  describeChange,
  describeCreated,
  formatWhen,
  recordLink,
} from '../../lib/history';
import RestoreFromHistory from './RestoreFromHistory';

/** Lines shown before "Show all". */
const PREVIEW_LINES = 6;

/**
 * One line of history: who did what to which record, when, and the changes.
 * Shared by a record's History drawer and Settings → History.
 *
 *   group        from groupHistory()
 *   showRecord   name the record (always in Settings; in a drawer, only for children)
 *   linkRecord   link the record name to where it can be opened
 *   onRestored   called after a Restore from this line
 */
export default function HistoryGroup({ group, showRecord = true, linkRecord = false, onRestored, now }) {
  const [expanded, setExpanded] = useState(false);

  const lines = group.baseline
    ? describeChange({ baseline: true })
    : group.action === 'create'
      ? describeCreated(group.changes)
      : group.changes.flatMap(describeChange);
  const visible = expanded ? lines : lines.slice(0, PREVIEW_LINES);

  const first = group.entries[group.entries.length - 1];
  const link = linkRecord ? recordLink(group.tableName, group.rowId, first.parentTable, first.parentRowId) : null;
  const recordName = group.label || TABLE_LABELS[group.tableName];

  return (
    <li className="py-3" data-testid="history-group">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 text-sm">
          <span className="font-medium">{actorOf(group)}</span>
          <span className="text-muted-foreground">
            {' '}{group.baseline ? 'Changed' : ACTION_LABELS[group.action] || group.action}
          </span>
          {showRecord && (
            <>
              {' '}
              {link
                ? <Link to={link} className="font-medium underline-offset-2 hover:underline">{recordName}</Link>
                : <span className="font-medium">{recordName}</span>}
            </>
          )}
          <div className="text-xs text-muted-foreground" title={new Date(group.at).toLocaleString('en-NZ')}>
            {formatWhen(group.at, now)}
            {group.entries.length > 1 && ` · ${group.entries.length} changes`}
          </div>
        </div>
        {group.action === 'delete' && (
          <RestoreFromHistory table={group.tableName} rowId={group.rowId} onRestored={onRestored} />
        )}
      </div>

      {visible.length > 0 && (
        <dl className="mt-2 space-y-1 border-l-2 border-muted pl-3 text-sm">
          {visible.map((line, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <div key={i} className="flex flex-wrap gap-x-2">
              {line.field && <dt className="text-muted-foreground">{line.field}</dt>}
              <dd className="min-w-0 break-words">{line.text}</dd>
            </div>
          ))}
        </dl>
      )}
      {lines.length > PREVIEW_LINES && (
        <button
          type="button"
          className="mt-1 pl-3 text-xs text-muted-foreground underline-offset-2 hover:underline"
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? 'Show less' : `Show all ${lines.length}`}
        </button>
      )}
    </li>
  );
}
